import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { useUpdateAdMutation } from "../../../services/ad";

const NewCampaignModal = ({
  open,
  onClose,
  adAccountId,
  onSuccess,
  adSetGlid,
}) => {
  const [name, setName] = useState("");
  const [budgetOpt, setBudgetOpt] = useState("CBO");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [updateAd] = useUpdateAdMutation();

  const isNameValid = name.trim().length > 0;
  const isBudgetValid =
    budgetOpt === "CBO"
      ? !!budget && !isNaN(budget) && Number(budget) > 0
      : true;
  const canSave = isNameValid && isBudgetValid && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = [
        {
          action: "create_campaign",
          ad_account_id: adAccountId,
          budget_optimization: budgetOpt,
          name: name.trim(),
          ...(budgetOpt === "CBO" ? { daily_budget: Number(budget) } : {}),
          ad_set_id: adSetGlid,
        },
      ];
      const result = await updateAd(payload).unwrap();
      setSaving(false);
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e?.data?.message || "Failed to create campaign");
    }
  };

  const handleCancel = () => {
    setName("");
    setBudgetOpt("CBO");
    setBudget("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a New Campaign</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Ad Account</div>
            <Input value={adAccountId} disabled className="bg-muted" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              Name <span className="text-red-500">*</span>
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Campaign name"
              autoFocus
              required
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              Budget Optimization
            </div>
            <RadioGroup
              value={budgetOpt}
              onValueChange={setBudgetOpt}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="ABO" id="abo" />
                <label htmlFor="abo" className="text-sm">
                  ABO
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="CBO" id="cbo" />
                <label htmlFor="cbo" className="text-sm">
                  CBO
                </label>
              </div>
            </RadioGroup>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              Budget (Euros)
            </div>
            <Input
              value={budget}
              onChange={(e) => setBudget(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="e.g. 100"
              disabled={budgetOpt === "ABO"}
              type="number"
              min="0"
              step="0.01"
            />
            {budgetOpt === "CBO" && !isBudgetValid && (
              <div className="text-xs text-red-500 mt-1">
                Enter a positive budget
              </div>
            )}
          </div>
          {error && <div className="text-xs text-red-500">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewCampaignModal;
